import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Save, Users, Scale, Briefcase, Wrench, Calculator } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const PIPES = [["sale", "Sale"], ["purchase", "Purchase"], ["tenancy", "Tenancy"], ["maintenance", "Maintenance"]];
const DEAL_PIPES = PIPES.slice(0, 3);

export default function Allocations() {
  const [data, setData] = useState({ power_users: [], legal_reps: [], accountants: [], brokers: [], caretakers: [], clients: [] });
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [pipeDrafts, setPipeDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/allocations");
      const scoped = [...(res.data.legal_reps || []), ...(res.data.accountants || []), ...(res.data.brokers || []), ...(res.data.caretakers || [])];
      setData({ power_users: res.data.power_users || [], legal_reps: res.data.legal_reps || [], accountants: res.data.accountants || [], brokers: res.data.brokers || [], caretakers: res.data.caretakers || [], clients: res.data.clients || [] });
      const d = {};
      [...(res.data.power_users || []), ...scoped].forEach((p) => { d[p.id] = new Set(p.allocated_client_ids || []); });
      setDrafts(d);
      const pd = {};
      scoped.forEach((p) => {
        const m = {};
        Object.entries(p.client_pipelines || {}).forEach(([cid, arr]) => { m[cid] = new Set(arr || []); });
        pd[p.id] = m;
      });
      setPipeDrafts(pd);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load allocations");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (uid, clientId) => {
    setDrafts((prev) => {
      const next = new Set(prev[uid]);
      next.has(clientId) ? next.delete(clientId) : next.add(clientId);
      return { ...prev, [uid]: next };
    });
  };

  const togglePipe = (uid, cid, pk) => {
    setPipeDrafts((prev) => {
      const um = { ...(prev[uid] || {}) };
      const s = new Set(um[cid] || []);
      s.has(pk) ? s.delete(pk) : s.add(pk);
      um[cid] = s;
      return { ...prev, [uid]: um };
    });
  };

  const save = async (uid, isLegal) => {
    setSavingId(uid);
    try {
      const body = { client_ids: Array.from(drafts[uid] || []) };
      if (isLegal) {
        const cp = {};
        Object.entries(pipeDrafts[uid] || {}).forEach(([cid, s]) => { cp[cid] = Array.from(s); });
        body.client_pipelines = cp;
      }
      await api.put(`/admin/allocations/${uid}`, body);
      toast.success("Allocation saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSavingId(null); }
  };

  // NOTE: rendered via plain functions (not nested components) so state updates
  // don't remount the subtree — this keeps scroll position & focus stable on toggle.
  const renderCard = (u, isLegal, pipes = PIPES) => {
    const sel = drafts[u.id] || new Set();
    return (
      <div key={u.id} data-testid="allocation-card" className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
          <span className="h-10 w-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-semibold">{(u.name || "?").charAt(0)}</span>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">{u.name}</div>
            <div className="text-xs text-slate-500 font-mono truncate">{u.email}</div>
          </div>
          <span className="ml-auto text-xs font-mono text-slate-500">{sel.size} allocated</span>
        </div>
        {isLegal && <div className="px-6 pt-3 text-xs text-slate-500">Toggle a client, then choose which pipelines that client is visible for.</div>}
        <div className="p-4 max-h-80 overflow-y-auto space-y-1">
          {data.clients.length === 0 && <div className="p-4 text-sm text-slate-400 text-center">No clients available.</div>}
          {data.clients.map((c) => {
            const on = sel.has(c.id);
            return (
              <div key={c.id} data-testid="allocation-client-row"
                className={`rounded-lg px-3 py-2.5 ${on ? "bg-emerald-50 border border-emerald-200" : "border border-transparent hover:bg-slate-50"}`}>
                <button type="button" onClick={() => toggle(u.id, c.id)} data-testid="allocation-client-toggle"
                  className="w-full flex items-center gap-3 text-left">
                  <span className={`h-5 w-5 rounded flex items-center justify-center shrink-0 ${on ? "bg-emerald-600 text-white" : "border border-slate-300"}`}>
                    {on && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm text-slate-800 truncate">{c.name}</span>
                    <span className="block text-xs text-slate-400 font-mono truncate">{c.email}</span>
                  </span>
                </button>
                {isLegal && on && (
                  <div className="mt-2 pl-8 flex flex-wrap gap-1.5" data-testid="client-pipeline-toggles">
                    {pipes.map(([pk, lbl]) => {
                      const pon = (pipeDrafts[u.id]?.[c.id] || new Set()).has(pk);
                      return (
                        <button key={pk} type="button" onClick={() => togglePipe(u.id, c.id, pk)} data-testid={`pipe-${pk}`}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${pon ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>{lbl}</button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="px-6 py-4 border-t border-slate-100">
          <button onClick={() => save(u.id, isLegal)} disabled={savingId === u.id} data-testid="allocation-save-button"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-all shadow-sm disabled:opacity-60 active:scale-[0.98]">
            {savingId === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Allocation
          </button>
        </div>
      </div>
    );
  };

  const renderGroup = (title, Icon, list, testid, empty, isLegal, pipes) => (
    <div className="mt-10" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-emerald-600" />
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      </div>
      {list.length === 0 ? (
        <div className="mt-4 p-8 text-center text-slate-500 border border-dashed border-slate-300 rounded-2xl text-sm">{empty}</div>
      ) : (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {list.map((u) => renderCard(u, isLegal, pipes))}
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-5xl" data-testid="allocations-page">
      <p className="text-xs uppercase tracking-[0.2em] font-mono text-emerald-700 font-semibold">Admin</p>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight font-display text-slate-900">Client Allocation</h1>
      <p className="mt-2 text-sm text-slate-500">Allocate clients to a power user or legal rep. For legal reps, accountants, brokers and caretakers, choose per-client which pipelines they may access.</p>
      {loading ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : (
        <>
          {renderGroup("Power Users", Users, data.power_users, "allocation-group-power", "No power users yet. Create one in User Management first.", false)}
          {renderGroup("Legal Reps", Scale, data.legal_reps, "allocation-group-legal", "No legal reps yet. Create one in User Management first.", true, DEAL_PIPES)}
          {renderGroup("Accountants", Calculator, data.accountants, "allocation-group-accountant", "No accountants yet. Create one in User Management first.", true, DEAL_PIPES)}
          {renderGroup("Brokers", Briefcase, data.brokers, "allocation-group-broker", "No brokers yet. Create one in User Management first.", true)}
          {renderGroup("Caretakers", Wrench, data.caretakers, "allocation-group-caretaker", "No caretakers yet. Create one in User Management first.", true, [["maintenance", "Maintenance"]])}
        </>
      )}
    </div>
  );
}
